import {
  Alert,
  assertIsFile,
  Body1,
  Body2,
  DoubleDrawer,
  DoubleDrawerPrimaryContent,
  H2,
  H4,
  colorsLight,
} from "@coherehealth/common";
import { UploadAuditCasesResponse, useUploadServiceCaseForAudit } from "@coherehealth/qm-api";
import { CircularProgress, Divider, Grid } from "@mui/material";
import React, { useContext, useEffect, useState, useRef } from "react";
import { User, UserContext } from "UserContext";
import { cohereBaseTheme } from "@coherehealth/design-system";
import { UploadedFileItem } from "./UploadedFileItem";
import { DrawerFooterButton } from "./DrawerFooterButton";
import { UploadFileButton } from "./UploadFileButton";
import { useSnackbar } from "notistack";
import { makeStyles } from "tss-react/mui";
import { ErrorMessageEmbedded } from "@coherehealth/core-platform-api";
import { MutateMethod } from "restful-react";
import { AuditTagSelect } from "./AuditTagSelect";

type UploadCaseDrawerProps = {
  open: boolean;
  onClose: () => void;
  queueId: string;
};

const useStyles = makeStyles()((theme) => ({
  drawer: {
    width: "485px",
    padding: theme.spacing(10, 4, 0, 4),
  },
  body1: {
    marginTop: theme.spacing(2),
    color: colorsLight.font.secondary,
  },
}));

export const UploadCaseDrawer = ({ open, onClose, queueId }: UploadCaseDrawerProps) => {
  const [file, setFile] = useState<File | null>(null);
  const { getUser } = useContext(UserContext);
  const [data, setData] = useState<UploadAuditCasesResponse | null>(null);
  const [error, setError] = useState<ErrorMessageEmbedded | null>(null);

  const [user, setUser] = useState<User>();
  const [auditTag, setAuditTag] = useState<string>("");
  const isMounted = useRef(true);
  const { enqueueSnackbar } = useSnackbar();
  const { mutate: uploadServiceCaseForAudit, loading: uploadingServiceCase } = useUploadServiceCaseForAudit({}) as {
    mutate: MutateMethod<UploadAuditCasesResponse, void, void, void>;
    loading: boolean;
    error: ErrorMessageEmbedded;
  };

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const handleClick = async () => {
    if (file) {
      const formData = new FormData();
      formData.set("file", file);
      formData.set("queueId", queueId);
      if (auditTag) {
        formData.set("auditTag", auditTag);
      }
      try {
        const response = await uploadServiceCaseForAudit(formData as unknown as void);
        setData(response);
      } catch (error: any) {
        setError(error);
      }
    }
  };

  const handleResetDrawer = () => {
    setFile(null);
    setError(null);
    setData(null);
    setAuditTag("");
  };
  const MAX_FILE_SIZE = 104857600;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const file = input?.files?.[0];
    if (file?.type !== "text/csv") {
      enqueueSnackbar("Not a CSV File", { variant: "error" });
      return;
    }
    if (file) {
      try {
        assertIsFile(file);
      } catch {
        enqueueSnackbar("Not a File", { variant: "error" });
        return;
      }

      if (file.size > MAX_FILE_SIZE) {
        enqueueSnackbar("Selected file is too large", { variant: "error" });
        return;
      }
      getUser().then((user) => {
        setUser(user);
        setFile(file);
      });
    }
  };

  const { classes } = useStyles();

  const getAllDuplicateCases = (
    preExistingCases: string[] | null,
    duplicatesInCsvFile: { [key: string]: number } | null
  ) => {
    let allDuplicateCases = preExistingCases || [];
    if (duplicatesInCsvFile) {
      allDuplicateCases = [...allDuplicateCases, ...Array.from(Object.keys(duplicatesInCsvFile))];
    }
    return allDuplicateCases.map((existingCase) => `#${existingCase}`).join(" ");
  };
  const getAllDuplicatesCohereIds = (duplicates = 0, duplicatesInCsvFile: { [key: string]: number } | null) => {
    let duplicateCohereIds = duplicates;
    if (duplicatesInCsvFile) {
      duplicateCohereIds =
        duplicateCohereIds +
        Array.from(Object.values(duplicatesInCsvFile)).reduce((accumulator, currentValue) => {
          return accumulator + currentValue - 1;
        }, 0);
    }
    return duplicateCohereIds;
  };
  const duplicates = data ? getAllDuplicatesCohereIds(data.duplicates, data.duplicatesInUpload || null) : 0;

  const getDuplicatesValidationMessage = (duplicateCohereIds: number) => {
    if (duplicateCohereIds > 1) {
      return `${duplicateCohereIds} cases were not uploaded due to being duplicates`;
    } else {
      return `${duplicateCohereIds} case was not uploaded due to being duplicate`;
    }
  };

  const getSuccessfulValidationMessage = (created: number) => {
    if (created > 1) {
      return `${created} cases were uploaded with the ${auditTag} audit tag`;
    } else {
      return `${created} case was uploaded with the ${auditTag} audit tag`;
    }
  };

  const getFailedValidationMessage = (failed: number) => {
    if (failed > 1) {
      return `${failed} cases were not able to be uploaded`;
    } else {
      return `${failed} case was not able to be uploaded`;
    }
  };

  return (
    <DoubleDrawer
      anchor="right"
      open={open}
      onClose={() => {
        handleResetDrawer();
        onClose();
      }}
    >
      <DoubleDrawerPrimaryContent className={classes.drawer}>
        <Grid
          container
          flexDirection="column"
          minHeight="100%"
          justifyContent={uploadingServiceCase || data ? "center" : "unset"}
          alignItems={uploadingServiceCase || data ? "center" : "unset"}
        >
          {!data && !uploadingServiceCase && !error && (
            <>
              <H4>Choose files to upload</H4>
              <Body1 className={classes.body1}>
                Upload .csv files to add cases to the audit queue. This upload should include an audit tag that will
                group these cases and an audit type to determine case assignment.
              </Body1>
              <AuditTagSelect value={auditTag} onChange={setAuditTag} />
              <Grid sx={{ marginTop: 3 }}>
                <Divider />
              </Grid>
              {file && <UploadedFileItem fileName={file.name} userName={user?.name} onDelete={() => setFile(null)} />}
              <Grid sx={{ marginTop: 3 }}>
                <UploadFileButton onChange={handleFileChange}>Upload .csv</UploadFileButton>
              </Grid>
              <Grid container flexDirection="column" justifyContent="end" flex="1" gap={5}>
                <Grid sx={{ marginTop: 3 }}>
                  <Divider />
                </Grid>
                <Grid sx={{ paddingBottom: 5 }}>
                  <DrawerFooterButton
                    disabled={!file || !auditTag?.trim()}
                    name="Finish upload"
                    onClick={handleClick}
                  />
                </Grid>
              </Grid>
            </>
          )}
          {data && (
            <>
              <Grid container flexDirection="column" alignItems="center" justifyContent="center" flex="1" gap={2}>
                <Grid>
                  <H2>Upload complete</H2>
                </Grid>
                {!!data.created && (
                  <Grid style={{ width: "100%" }}>
                    <Alert severity="success" message={getSuccessfulValidationMessage(data.created)}></Alert>
                  </Grid>
                )}
                {!!data?.failed && (
                  <Grid style={{ width: "100%" }}>
                    <Alert severity="info">
                      <>
                        <Body2 style={{ color: cohereBaseTheme.palette.info.main }}>
                          {getFailedValidationMessage(data.failed)}
                        </Body2>
                        <br />
                        <Body2 style={{ color: cohereBaseTheme.palette.info.main }}>
                          {`Tracking ID${data.failed > 1 ? "s" : ""}: ${data.failedCohereIds
                            ?.map((failedCohereId) => `#${failedCohereId}`)
                            .join(" ")}`}
                        </Body2>
                      </>
                    </Alert>
                  </Grid>
                )}
                {(!!data.duplicates || !!data.duplicatesInUpload) && (
                  <Grid style={{ width: "100%" }}>
                    <Alert severity="info">
                      <Body2 style={{ color: cohereBaseTheme.palette.info.main }}>
                        {getDuplicatesValidationMessage(duplicates)}
                      </Body2>
                      <br />
                      <Body2 style={{ color: cohereBaseTheme.palette.info.main }}>
                        {`Tracking ID${duplicates > 1 ? "s" : ""}: ${getAllDuplicateCases(
                          data.preExistingCases || null,
                          data.duplicatesInUpload || null
                        )}`}
                      </Body2>
                    </Alert>
                  </Grid>
                )}
              </Grid>
              <Grid container flexDirection="column" justifyContent="end" flex="0" gap={5}>
                <Grid sx={{ marginTop: 3 }}>
                  <Divider />
                </Grid>
                <Grid sx={{ paddingBottom: 5 }}>
                  <DrawerFooterButton
                    name="Close"
                    onClick={() => {
                      handleResetDrawer();
                      onClose();
                    }}
                  />
                </Grid>
              </Grid>
            </>
          )}
          {error && (
            <>
              <Grid container flexDirection="column" alignItems="center" justifyContent="center" flex="1" gap={2}>
                <Grid>
                  <H2>Upload incomplete</H2>
                </Grid>
                <Grid style={{ width: "100%" }}>
                  <Alert severity="error">
                    <Body2 style={{ color: cohereBaseTheme.palette.error.main }}>
                      {`Upload failed because of the following reason:`}
                    </Body2>
                    <br />
                    <Body2 style={{ color: cohereBaseTheme.palette.error.main }}>
                      {error?.data?._embedded?.errors?.map((error: any) => error?.message).join("\n")}
                    </Body2>
                  </Alert>
                </Grid>
              </Grid>
              <Grid container flexDirection="column" justifyContent="end" flex="0" gap={5}>
                <Grid sx={{ marginTop: 3 }}>
                  <Divider />
                </Grid>
                <Grid sx={{ paddingBottom: 5 }}>
                  <DrawerFooterButton
                    name="Close"
                    onClick={() => {
                      handleResetDrawer();
                      onClose();
                    }}
                  />
                </Grid>
              </Grid>
            </>
          )}
          {uploadingServiceCase && (
            <Grid container flexDirection={"column"} alignItems={"center"}>
              <Grid sx={{ marginBottom: 3 }}>
                <H2 align="center">Upload in progress, please don't close this page. This may take up to a minute.</H2>
              </Grid>
              <Grid flex="0">
                <CircularProgress size={80} />
              </Grid>
            </Grid>
          )}
        </Grid>
      </DoubleDrawerPrimaryContent>
    </DoubleDrawer>
  );
};
export default UploadCaseDrawer;
